import {
  CellState,
  ConstructionPhase,
  FACILITY_SPECS,
  FacilityManager,
  FiscalManager,
  FreightManager,
  LabourManager,
  MercyManager,
  WelfareManager,
  PowerManager,
  WaterManager,
} from '@antification/core';
import { ParkingtonTool, type ToolDefinition, type ParkingtonState } from '../ParkingtonTypes';

/**
 * Callbacks for HTML UI interactions that need to mutate orchestrator state.
 */
export interface HTMLUICallbacks {
  setTool(tool: ParkingtonTool): void;
  togglePanMode(): void;
  saveGame(): void;
  toggleSaveSlots(): void;
  hasSavedGame(): boolean;
}

/**
 * Manages HTML overlay elements for tools, status, and save/load.
 */
export class HTMLUIRenderer {
  private htmlToolButtons: Map<ParkingtonTool, HTMLElement> = new Map();
  private htmlPanButton: HTMLElement | null = null;
  private htmlStatusPhase: HTMLElement | null = null;
  private htmlStatusProgress: HTMLElement | null = null;
  private htmlStatusProgressText: HTMLElement | null = null;
  private htmlStatusStats: HTMLElement | null = null;
  private htmlUIFrameCounter = 0;
  private readonly HTML_UI_UPDATE_INTERVAL = 6;

  /**
   * Initialize HTML overlay elements.
   */
  initHTMLUI(state: ParkingtonState, callbacks: HTMLUICallbacks): void {
    const toolbar = document.getElementById('park-toolbar');
    if (toolbar) {
      toolbar.innerHTML = '';
      for (const tool of state.toolDefinitions) {
        const btn = document.createElement('button');
        btn.className = 'park-tool-btn' + (state.currentTool === tool.id ? ' active' : '') + (!tool.available ? ' locked' : '');
        btn.dataset.tool = tool.id;
        btn.title = `${tool.name} - ${tool.description}`;

        const iconSpan = document.createElement('span');
        iconSpan.className = 'tool-icon';
        iconSpan.textContent = tool.icon;
        btn.appendChild(iconSpan);

        const shortcutSpan = document.createElement('span');
        shortcutSpan.className = 'tool-shortcut';
        shortcutSpan.textContent = tool.shortcut;
        btn.appendChild(shortcutSpan);

        if (tool.id === ParkingtonTool.PRECISION && !tool.available) {
          const progressDiv = document.createElement('div');
          progressDiv.className = 'tool-progress';
          const fillDiv = document.createElement('div');
          fillDiv.className = 'tool-progress-fill';
          fillDiv.style.width = `${Math.min(100, (state.cellsChanged / state.PRECISION_UNLOCK) * 100)}%`;
          progressDiv.appendChild(fillDiv);
          btn.appendChild(progressDiv);
        }

        btn.addEventListener('click', () => {
          if (tool.available) {
            callbacks.setTool(tool.id);
            this.updateToolButtonStates(state);
          }
        });

        this.htmlToolButtons.set(tool.id, btn);
        toolbar.appendChild(btn);
      }

      // Pan mode toggle
      const sep = document.createElement('div');
      sep.className = 'park-tool-sep';
      toolbar.appendChild(sep);

      const panBtn = document.createElement('button');
      panBtn.className = 'park-tool-btn park-pan-toggle';
      panBtn.title = 'Pan Mode (M) - Drag to move viewport';
      const panIcon = document.createElement('span');
      panIcon.className = 'tool-icon';
      panIcon.textContent = '✋';
      panBtn.appendChild(panIcon);
      const panShortcut = document.createElement('span');
      panShortcut.className = 'tool-shortcut';
      panShortcut.textContent = 'M';
      panBtn.appendChild(panShortcut);
      panBtn.addEventListener('click', () => {
        callbacks.togglePanMode();
      });
      this.htmlPanButton = panBtn;
      toolbar.appendChild(panBtn);
    }

    // Status panel elements
    this.htmlStatusPhase = document.querySelector('#park-status .status-phase-name');
    this.htmlStatusProgress = document.querySelector('#park-status .status-progress-fill');
    this.htmlStatusProgressText = document.querySelector('#park-status .status-progress-text');
    this.htmlStatusStats = document.querySelector('#park-status .status-stats');

    // Save/load buttons
    const saveBtn = document.querySelector('#park-actions .save-btn');
    const loadBtn = document.querySelector('#park-actions .load-btn');
    if (saveBtn) {
      saveBtn.addEventListener('click', () => {
        callbacks.saveGame();
      });
    }
    if (loadBtn) {
      loadBtn.addEventListener('click', () => {
        if (callbacks.hasSavedGame()) {
          callbacks.toggleSaveSlots();
        }
      });
    }
  }

  /**
   * Update tool button states (active, locked, progress).
   */
  updateToolButtonStates(state: ParkingtonState): void {
    for (const tool of state.toolDefinitions) {
      const btn = this.htmlToolButtons.get(tool.id);
      if (!btn) continue;
      btn.className = 'park-tool-btn' + (state.currentTool === tool.id ? ' active' : '') + (!tool.available ? ' locked' : '');

      if (tool.id === ParkingtonTool.PRECISION) {
        const progressFill = btn.querySelector('.tool-progress-fill') as HTMLElement;
        if (progressFill) {
          progressFill.style.width = `${Math.min(100, (state.cellsChanged / state.PRECISION_UNLOCK) * 100)}%`;
        }
        if (tool.available) {
          const progressBar = btn.querySelector('.tool-progress');
          if (progressBar) progressBar.remove();
        }
      }
    }
  }

  /**
   * Update pan button appearance.
   */
  updatePanButtonState(isPanMode: boolean): void {
    if (this.htmlPanButton) {
      this.htmlPanButton.classList.toggle('active', isPanMode);
    }
  }

  /**
   * Update HTML overlay UI elements (throttled).
   */
  updateHTMLUI(state: ParkingtonState, getPhaseLabel: (phase: ConstructionPhase) => string): void {
    this.htmlUIFrameCounter++;
    if (this.htmlUIFrameCounter % this.HTML_UI_UPDATE_INTERVAL !== 0) return;

    const phase = state.construction.getCurrentPhase();
    const progress = state.construction.getPhaseProgress();

    if (this.htmlStatusPhase) {
      this.htmlStatusPhase.textContent = getPhaseLabel(phase);
    }

    if (this.htmlStatusProgress) {
      this.htmlStatusProgress.style.width = `${Math.floor(progress * 100)}%`;
    }

    if (this.htmlStatusProgressText) {
      this.htmlStatusProgressText.textContent = `${Math.floor(progress * 100)}% Complete`;
    }

    if (this.htmlStatusStats) {
      const counts = state.grid.countCellsByState();
      const hasPaved = counts[CellState.POURED] > 0 ||
                       counts[CellState.CURED] > 0 ||
                       counts[CellState.PAINTED] > 0;

      if (hasPaved) {
        const snap = state.economy.getBudgetSnapshot(state.houses);
        this.htmlStatusStats.innerHTML = this.buildBudgetHTML(
          snap, state.season, state.power, state.water, state.facilities, state.labour,
          state.welfare, state.mercy, state.fiscal, state.freight
        );
      } else {
        this.htmlStatusStats.innerHTML = '<span class="status-hint">Click cells to develop (4 clicks → parking)</span>';
      }
    }

    // Update load button state
    const loadBtn = document.querySelector('#park-actions .load-btn');
    if (loadBtn) {
      if (state.construction.getCurrentPhase() !== undefined) {
        try {
          const json = localStorage.getItem('antification-saves');
          const legacyJson = localStorage.getItem('antification-save');
          if (json || legacyJson) {
            loadBtn.classList.remove('disabled');
          } else {
            loadBtn.classList.add('disabled');
          }
        } catch {
          loadBtn.classList.add('disabled');
        }
      }
    }

    this.updateToolButtonStates(state);
  }

  /**
   * Build the enhanced budget display HTML from a budget snapshot.
   */
  private buildBudgetHTML(snap: {
    governmentPool: number;
    lastCycleRevenue: number;
    lastCycleReceipts: number;
    lastCycleSpending: number;
    lastCycleNet: number;
    lastCycleInterest: number;
    roadQuality: number;
    familyCount: number;
    avgFamilyCash: number;
    corpCount: number;
  },
    season: number,
    power: PowerManager,
    water: WaterManager,
    facilities: FacilityManager,
    labour: LabourManager,
    welfare: WelfareManager,
    mercy: MercyManager,
    fiscal: FiscalManager,
    freight: FreightManager
  ): string {
    const pool = Math.floor(snap.governmentPool);
    const poolClass = pool >= 200 ? 'budget-ok' : pool >= 0 ? 'budget-warn' : 'budget-deficit';

    const rev = Math.floor(snap.lastCycleRevenue);
    const exp = Math.floor(snap.lastCycleSpending);
    const net = Math.floor(snap.lastCycleNet);
    const netSign = net >= 0 ? '+' : '';
    const interest = Math.floor(snap.lastCycleInterest);

    const rq = Math.round(snap.roadQuality);
    const rqPct = Math.max(0, Math.min(100, rq));
    const rqColor = rq >= 60 ? '#4caf50' : rq >= 30 ? '#ff9800' : '#f44336';

    // The coarsest of the district's three clocks, in front of the money it
    // will eventually be settled against. Ninety budget cycles to a season; a
    // player at the client's cadence sees this tick over about every fifteen
    // minutes. See `docs/seasons.md`.
    let html = `<span class="status-season">Season ${season} ·</span> `;
    html += `<span class="${poolClass}">Gov: $${pool}</span>`;
    if (interest > 0) {
      html += ` <span class="budget-deficit">(interest: $${interest})</span>`;
    }
    html += `<br>`;
    html += `<span class="budget-flow">Rev: $${rev} | Exp: $${exp} | Net: ${netSign}$${net}</span><br>`;

    // Road quality bar
    html += `<span class="road-quality-row">Roads: <span class="road-quality-bar"><span class="road-quality-fill" style="width:${rqPct}%;background:${rqColor}"></span></span> ${rq}%</span><br>`;

    // Population stats
    html += `<span class="status-pop">Families: ${snap.familyCount} (avg $${Math.floor(snap.avgFamilyCash)}) | Corps: ${snap.corpCount}</span>`;

    // The grid, in the same register as everything else on this panel: two
    // counted numbers and no conclusion. It appears once a cycle has been
    // metered, so an untouched meadow does not advertise a utility bill.
    const demand = power.getDemand().total;
    const supply = power.getSupplyKw();
    if (power.getLedger().meteredCycles > 0) {
      const dark = power.getShedCount();
      const short = power.getShedKw() > 0;
      html += `<br><span class="status-grid${short ? ' status-grid-short' : ''}">`;
      html += `⚡ ${Math.round(demand)} / ${Math.round(supply)} kW`;
      if (dark > 0) html += ` · ${dark} dark`;
      html += `</span>`;
    }

    // The mains, in the same register and on the same terms. Brown lawns are
    // reported before dry taps because that is the order they happen in, and
    // the rainfall is deliberately not here: a drought is something a player
    // sees on the map, not something the panel explains.
    if (water.getLedger().meteredCycles > 0) {
      const short = water.getCutM3() > 0;
      const brown = water.getParchedCount();
      const dry = water.getDryCount();
      html += `<br><span class="status-mains${short ? ' status-mains-short' : ''}">`;
      html += `💧 ${Math.round(water.getDemand().total)} / ` +
              `${Math.round(water.getSupplyM3())} m³`;
      if (brown > 0) html += ` · ${brown} brown`;
      if (dry > 0) html += ` · ${dry} dry`;
      html += `</span>`;
    }

    // The federal estate, on the same terms as the two flows above and appearing
    // only when there is one. Two counted numbers and no conclusion: the land the
    // polity took, and how much of what its machines asked for they got. There is
    // no output figure because there is no output, and the panel does not say
    // whose facility it is or what it is for — nothing in Parkington has ever
    // explained itself to the mayor.
    if (facilities.count > 0) {
      const uptime = Math.round(facilities.getUptime().uptime * 100);
      // Named while there is one of them, counted once there are several. The
      // name is what it *is*, not whose it is — nothing here says a polity built
      // it, because nothing in Parkington has ever explained itself to the mayor.
      const what = facilities.count === 1
        ? FACILITY_SPECS[facilities.getFacilities()[0].type].label
        : `${facilities.count} facilities`;
      html += `<br><span class="status-estate${facilities.curtailed ? ' status-estate-short' : ''}">`;
      html += `🏢 ${what} · ${facilities.sealedCells} cells`;
      // A building site is not a facility, and the line says which it is looking
      // at before it says anything else about it. What it does *not* say is how
      // many seasons are left: the goods figure is a count of things that have
      // arrived on the ground, and a countdown would be the panel explaining
      // somebody else's schedule to a mayor who was never told there was one.
      const building = facilities.count - facilities.operatingCount;
      if (building > 0) {
        const goods = facilities.getFacilities()
          .filter((f) => f.openedTick === null)
          .reduce((sum, f) => sum + facilities.getBuild(f.id).hoard, 0);
        const needs = facilities.getFacilities()
          .filter((f) => f.openedTick === null)
          .reduce((sum, f) => sum + facilities.goodsPerSeasonFor(f.type), 0);
        html += ` · ${building} building · ${Math.floor(goods)}/${needs} goods`;
      }
      if (facilities.operatingCount > 0) html += ` · ${uptime}% supplied`;
      // Two of the town's own families out of the eight the machine needs, and
      // whether they are going. The strike is the one thing on this panel the
      // mayor did rather than had done to them, so it is the one thing named in
      // words — and even then the panel does not say whether it was a good idea.
      const staffing = labour.getStaffing();
      if (staffing.posts > 0) {
        html += ` · ${staffing.staffed}/${staffing.posts} staff`;
      }
      if (labour.strikes().length > 0) html += ` · withdrawn`;
      // The rent. The only figure on this panel that is money coming *in* from
      // outside the district, and the only thing on the map the mayor did not
      // build that pays them anything at all.
      const rent = facilities.getLeasePerCycle();
      if (rent > 0) html += ` · +$${rent.toFixed(2)}/cy`;
      html += `</span>`;
    }

    // The households, and only once one of them is in trouble.
    //
    // The same rule the two flows and the estate follow, applied to the line
    // that would be most tempting to leave permanently on screen: a district
    // where everybody is solvent, lit and watered says nothing about its
    // quality of life, because there is nothing measured to say. When it does
    // appear it is two numbers and no verdict — a score the panel does not
    // explain, and a count of households under the line that the panel does not
    // say the mayor should do anything about.
    //
    // Deliberately *not* beside the existing Stakeholder Satisfaction meter,
    // which reads 98% 😊 forever and is the joke it has always been. §12 puts
    // District Vitals directly beneath it, and that is a Phase 4 screen.
    if (welfare.reported) {
      const score = welfare.meanQol();
      const strained = welfare.strainedCount();
      const left = welfare.getLedger().departures;
      html += `<br><span class="status-vitals${strained > 0 ? ' status-vitals-strained' : ''}">`;
      html += `🏠 QoL ${score === null ? '—' : score.toFixed(2)}`;
      if (strained > 0) html += ` · ${strained} struggling`;
      if (left > 0) html += ` · ${left} left`;
      // The other half of migration, and the half `Families:` above cannot show:
      // a population that holds steady because nobody left and one that holds
      // steady because nobody would come are the same number up there.
      const refused = welfare.getLedger().arrivalsRefused;
      if (refused > 0) html += ` · nobody moving in`;
      html += `</span>`;
    }

    // The works, and only once the mayor has bought one.
    //
    // The same rule again, and here it is doing something the other lines are not:
    // this is the only place in the interface where any of the three mercy builds can
    // be seen as a quantity at all. The stones and the arches are on the map; what it
    // has *come to* is here, in dollars and cells, with no ratio, no per-cell figure,
    // and nothing it is measured against.
    //
    // Because there is nothing to measure it against. §10's `0` revenue column is the
    // point, and a panel that put mercy beside a benefit would be a panel that had
    // invented one.
    if (!mercy.empty) {
      const ledger = mercy.getLedger();
      html += `<br><span class="status-works">`;
      html += `⌇ Works $${Math.round(ledger.spent).toLocaleString('en-US')}`;
      if (mercy.deededCells > 0) html += ` · ${mercy.deededCells} deeded`;
      if (mercy.saplingCells > 0) html += ` · ${mercy.saplingCells} growing`;
      if (ledger.hedgerowsGrown > 0) html += ` · ${ledger.hedgerowsGrown} grown`;
      if (mercy.crossingCells > 0) html += ` · ${mercy.crossingCells} crossings`;
      // The one figure here that is about somebody else, and the only trace in the
      // whole interface of §10's condemnation.
      if (ledger.condemnedCells > 0) html += ` · ${ledger.condemnedCells} condemned`;
      html += `</span>`;
    }

    // The account with the polity, and only once something has been billed — which is
    // any district with a building on it, so in practice always.
    //
    // Three things, in the order a mayor needs them. The **bill** is a cost like any
    // other and reads like one. The **dial** is not the mayor's, and the line says the
    // rate rather than what it will cost, because what it will cost is arithmetic
    // nothing here should do for them. And the **arrears** are the only figure in this
    // panel that explains another one: `Roads:` above is what it is because this is
    // what it is, and the two are three lines apart on purpose.
    //
    // No projection, no warning, no colour on the dial. §12's Ledger Dock is where a
    // mayor is supposed to be told what is coming, and it is Phase 4's.
    if (!fiscal.empty) {
      const account = fiscal.getLedger();
      const bill = account.powerBilled + account.waterBilled;
      const owed = Math.round(fiscal.getOwed());
      const rate = fiscal.getTitheRate();
      html += `<br><span class="status-fiscal">`;
      html += `§ Bills $${Math.round(bill).toLocaleString('en-US')}`;
      if (rate > 0) html += ` · tithe ${Math.round(rate * 100)}%`;
      else if (rate < 0) html += ` · grant ${Math.round(-rate * 100)}%`;
      if (account.tithePaid > 0) {
        html += ` · remitted $${Math.round(account.tithePaid).toLocaleString('en-US')}`;
      }
      if (account.grantsReceived > 0) {
        html += ` · received $${Math.round(account.grantsReceived).toLocaleString('en-US')}`;
      }
      if (owed > 0) {
        html += `<br><span class="status-fiscal-owed">roads unfunded — ` +
                `$${owed.toLocaleString('en-US')} outstanding</span>`;
      }
      html += `</span>`;
    }

    // The corridor, once there is one. Two numbers a mayor bought and two they did
    // not, in the same flat municipal grey the rest of the panel uses.
    //
    // The toll and the wear sit side by side and are deliberately in different
    // units — dollars against points of road quality — because they are the two
    // halves of the same bargain and netting them would be the interface taking a
    // position on whether it was a good one. `Roads:` four lines above already shows
    // where the second half landed.
    //
    // What is *not* here: how many trucks are on the map right now, what they are
    // carrying, and where they are going. A mayor can see the trucks. Nothing in
    // this build tells them how many of them killed something.
    if (freight.active) {
      const corridor = freight.getLedger();
      html += `<br><span class="status-freight">`;
      html += `⇹ Freight ${corridor.interchanges} ` +
              `interchange${corridor.interchanges === 1 ? '' : 's'}`;
      if (corridor.legsCompleted > 0) html += ` · ${corridor.legsCompleted} crossings`;
      if (corridor.tollsEarned > 0) {
        html += ` · tolls $${Math.round(corridor.tollsEarned).toLocaleString('en-US')}`;
      }
      if (corridor.roadWear > 0) html += ` · wear ${corridor.roadWear.toFixed(1)} pts`;
      // Legs the polity routed at a district that could not take them. It is the
      // one figure here a mayor can act on: build another interchange, or clear
      // the gate.
      if (corridor.legsLapsed > 0) html += ` · ${corridor.legsLapsed} turned away`;
      html += `</span>`;
    }

    return html;
  }
}
